import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../../utils/formatDate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import filterIcon from "../../assets/Images/Filter.png";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";

import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../../utils/toastMessages";
import approveIcon from "../../assets/icons/Approve.png";
import rejectIcon from "../../assets/icons/Reject.png";
import { BsArrowRepeat } from "react-icons/bs";
import fileIcon from "../../assets/icons/sboxfile.png";
import linkIcon from "../../assets/icons/sboxlink.png";
import crossIcon from "../../assets/icons/crossIcon.png";
import viewIcon from "../../assets/Images/View.png";
import { formatFileType } from "../../utils/FileType";
import { useSelector, useDispatch } from "react-redux";

const UploadedDocs = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const isFullPrivacy = useSelector((state) => state.privacy.isFullPrivacy);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [summaryDataList, setSummaryDataList] = useState([]);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [currentAction, setCurrentAction] = useState(""); // "approved" or "rejected"
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Add a loading state for each summary
  const [loadingIndex, setLoadingIndex] = useState(null);

  const navigate = useNavigate();

  const location = useLocation();

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  useEffect(() => {
    const fetchUploadedDocs = async () => {
      try {
        const token =
          localStorage.getItem("access_token") ||
          sessionStorage.getItem("access_token");
        const res = await api.get(
          `${process.env.REACT_APP_API_URL}/uploaded-files/global-pending-files`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        // console.log("Fetched Uploaded Docs:", res.data.data);
        setUploadedDocs(res.data.data);
      } catch (error) {
        console.error("Error fetching uploaded documents", error);
      }
    };

    fetchUploadedDocs();
  }, []);

  const fetchSummaryFromBackend = async (uploadIds) => {
    try {
      const idsQuery = uploadIds.join(","); // These are python_file_uuid values
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/uploaded-files/summary?ids=${idsQuery}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // console.log("Fetched summaries:", res.data);
      setSummaryDataList(res.data);
      setShowSummaryModal(true);
    } catch (err) {
      console.error("Failed to fetch summaries:", err);
      showErrorToast("Failed to fetch summaries.");
    }
  };

  const regenerateSummary = async (fileId, index) => {
    try {
      setLoadingIndex(index); // mark this item as loading

      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/uploaded-files/regenerate-summary`,
        { file_id: fileId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Update the specific summary in the list
      const updatedList = [...summaryDataList];
      updatedList[index].summary = res.data.summary;
      setSummaryDataList(updatedList);

      showSuccessToast("Summary regenerated!");
    } catch (err) {
      console.error("Failed to regenerate summary:", err);
      showErrorToast("Regeneration failed.");
    } finally {
      setLoadingIndex(null); // reset loading
    }
  };

  const handleApproveOrReject = async (requestId, action) => {
    setShowSummaryModal(false);
    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/uploaded-files/approve-or-reject`,
        {
          request_id: requestId,
          action,
          notes: notes || "",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      showSuccessToast(
        response.data.message || `Document ${action} successfully`
      );

      // Refresh list
      setUploadedDocs((prev) =>
        prev.filter((doc) => doc.approval_request_id !== requestId)
      );

      setShowActionModal(false);
    } catch (error) {
      console.error(`Error during ${action}:`, error);
      showErrorToast(
        error.response?.data?.message || `Failed to ${action} document`
      );
    }
  };

  const filteredDocs = uploadedDocs
    .filter((doc) => {
      const search = searchTerm.toLowerCase();
      return (
        doc.name?.toLowerCase().includes(search) ||
        doc.uploadedBy?.toLowerCase().includes(search)
      );
    })
    .filter((doc) => {
      if (!dayFilter) return true;
      const updatedDate = new Date(doc.updatedAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dayFilter === "Today") return updatedDate >= today;
      if (dayFilter === "Yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return updatedDate >= yesterday && updatedDate < today;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  const totalEntries = filteredDocs.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredDocs.slice(indexOfFirst, indexOfLast);

  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleReset = () => {
    setSortOrder("desc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  return (
    <div className="mx-2 pt-2 h-100">
      {/* <div className="d-flex justify-content-between align-items-center mb-2 mt-1 ">
        <span className="refresh-prompts fw-bold" style={{ fontSize: "20px" }}>
          Uploaded Documents
        </span>
      </div> */}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        <div className="d-flex align-items-center">
          <div className="custom-search-wrapper me-3">
            <div
              className="input-group custom-search-group"
              style={{ width: "320px" }}
            >
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <div className="">
            <button
              className="icon-button filter-dropdown-toggle action-button"
              type="button"
              data-bs-toggle="dropdown"
            >
              <img src={filterIcon} className="icon-image" alt="Filter" />
            </button>

            <div
              className="dropdown-menu profile-menu-dropdown p-2"
              style={{ minWidth: "200px" }}
            >
              <h6 className="fw-bolder refresh-prompts">Time</h6>
              <hr />
              {["asc", "desc"].map((order) => (
                <div className="form-check" key={order}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sortOrder"
                    id={order}
                    value={order}
                    checked={sortOrder === order}
                    onChange={() => setSortOrder(order)}
                  />
                  <label className="form-check-label" htmlFor={order}>
                    {order === "asc" ? "Ascending" : "Descending"}
                  </label>
                </div>
              ))}

              <hr />
              <h6 className="fw-bolderrefresh-prompts">By Day</h6>
              <hr />
              {["Today", "Yesterday", ""].map((day) => (
                <div className="form-check" key={day || "all"}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={day || "all"}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label className="form-check-label" htmlFor={day || "all"}>
                    {day || "All"}
                  </label>
                </div>
              ))}

              <hr />
              <div className="d-flex justify-content-between mt-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={() => setCurrentPage(1)}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="refresh-prompts ak-text-md">No entries found.</p>
        </div>
      ) : (
        <div className="not-disable1">
          <div className="table-responsive">
            <table className="table uploadedDoc-table table-sm w-100">
              <thead className="text-left">
                <tr>
                  <th>Document Name</th>
                  <th>Type</th>
                  <th>Size</th>
                  <th>Uploaded by</th>
                  <th>Last Updated</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((doc, i) => (
                  <tr key={i}>
                    <td
                      className="text-truncate text-left"
                      style={{ maxWidth: "150px" }}
                    >
                      {doc.documentName?.split(".").slice(0, -1).join(".") ||
                        doc.documentName}
                    </td>

                    <td data-label="Document Type" className="text-left">
                      {formatFileType(doc.type, doc.name)}
                    </td>
                    <td className="text-left">
                      {(doc.size / 1024).toFixed(2)} KB
                    </td>
                    <td className="text-left">{doc.uploadedBy || "N/A"}</td>
                    <td className="text-left">
                      {formatDateToUserTimeZone(doc.updatedAt)}
                    </td>
                    <td className="text-left">
                      <button
                        className="icon-button me-2"
                        title="Approved"
                        onClick={() => {
                          setCurrentRequestId(doc.id);
                          setCurrentAction("approved");
                          setNotes(""); // reset notes
                          setShowActionModal(true); // open custom modal
                        }}
                      >
                        <img src={approveIcon} alt="Approved" />
                      </button>

                      <button
                        className="icon-button"
                        title="Rejected"
                        onClick={() => {
                          setCurrentRequestId(doc.id);
                          setCurrentAction("rejected");
                          setNotes(""); // reset notes
                          setShowActionModal(true); // open custom modal
                        }}
                      >
                        <img src={rejectIcon} alt="Rejected" />
                      </button>

                      {/* <button
                        className="icon-button"
                        title="View Summary"
                        onClick={() => fetchSummaryFromBackend([doc.id])}
                      >
                        <img
                          src={viewIcon}
                          alt="summary view"
                          className="icon-image"
                        />
                      </button> */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table-footer bgcolor fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(1)}
                >
                  <i className="bi bi-chevron-double-left" />
                </button>
              </li>
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage - 1)}
                >
                  <i className="bi bi-chevron-left" />
                </button>
              </li>
              {pageNumbers.map((num) => (
                <li
                  key={num}
                  className={`page-item ${currentPage === num ? "active" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(num)}
                  >
                    {num}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(currentPage + 1)}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => handlePageClick(totalPages)}
                >
                  <i className="bi bi-chevron-double-right" />
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}

      {showSummaryModal && summaryDataList && (
        <div className="modal-backdrop-custom d-flex justify-content-center align-items-center">
          <div
            className="p-4 rounded-3 text-start position-relative"
            style={{
              backgroundColor: "#ffffff",
              width: "90%",
              maxWidth: "800px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <img
              src={crossIcon}
              alt="Close"
              className="custom-close-icon"
              onClick={() => setShowSummaryModal(false)}
            />

            <p className="fw-bold mt-2 ps-3 mb-3">
              Summaries from uploaded Documents and URL
            </p>

            {summaryDataList.length === 0 ? (
              <p>No summaries available.</p>
            ) : (
              summaryDataList.map((summary, index) => (
                <div key={index} className=" rounded-3 p-3 mb-3">
                  <div className="mb-2 d-flex align-items-center gap-3">
                    {summary.type === "file" ? (
                      <img
                        src={fileIcon}
                        alt="file"
                        className=""
                        style={{ width: "20px", height: "20px" }}
                      />
                    ) : (
                      <img
                        src={linkIcon}
                        alt="link"
                        className=""
                        style={{ width: "20px", height: "20px" }}
                      />
                    )}
                    <div>
                      <div
                        className="text-muted text-truncate"
                        title={summary.name}
                      >
                        {summary.name || "Untitled"}
                      </div>
                      <div
                        className="text-muted small text-truncate"
                        title={summary.link}
                      >
                        {summary.link || ""}
                      </div>
                    </div>
                  </div>

                  <div className="position-relative">
                    <textarea
                      style={{ fontSize: "14px" }}
                      className="form-control"
                      rows={5}
                      value={summary.summary || "No summary available."}
                      readOnly
                    />
                    <button
                      className="btn btn-link position-absolute text-white end-0 mt-1 bg-dark me-2 d-flex align-items-center"
                      onClick={() =>
                        regenerateSummary(summary.approval_request_id, index)
                      }
                      disabled={loadingIndex === index} // disable button while loading
                    >
                      {loadingIndex === index ? (
                        <span className="spinner-border spinner-border-sm text-white" />
                      ) : (
                        <BsArrowRepeat size={18} />
                      )}
                    </button>
                  </div>

                  <div className="d-flex justify-content-center gap-2 mt-4">
                    <button
                      className="btn btn-dark"
                      onClick={() => {
                        setShowSummaryModal(false); // ✅ Hide summary if open
                        setCurrentRequestId(summary.approval_request_id);
                        setCurrentAction("rejected");
                        setNotes("");
                        setShowActionModal(true);
                      }}
                    >
                      Reject
                    </button>

                    <button
                      className="btn btn-dark"
                      onClick={() => {
                        setShowSummaryModal(false); // ✅ Hide summary if open
                        setCurrentRequestId(summary.approval_request_id);
                        setCurrentAction("approved");
                        setNotes("");
                        setShowActionModal(true);
                      }}
                    >
                      Approve
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {showActionModal && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content w-50 mx-auto">
              <div className="modal-header justify-content-center position-relative">
                <h5 className="modal-title m-0">
                  {currentAction === "approved"
                    ? "Approve Document"
                    : "Reject Document"}
                </h5>
                <button
                  type="button"
                  className="modal-close-btn"
                  onClick={() => setShowActionModal(false)}
                >
                  <img src={crossIcon} className="icon-image" alt="Close" />
                </button>
              </div>

              <div className="modal-body">
                <label className="form-label">Optional Notes</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Enter review notes (optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                ></textarea>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="cancle-btn"
                  onClick={() => setShowActionModal(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="custom-btn"
                  disabled={isSubmitting}
                  onClick={async () => {
                    setIsSubmitting(true);
                    try {
                      const response = await api.post(
                        `${process.env.REACT_APP_API_URL}/uploaded-files/approve-or-reject`,
                        {
                          file_id: currentRequestId,
                          approval_status: currentAction, // approved or rejected
                          notes: notes || "",
                        },
                        {
                          headers: { Authorization: `Bearer ${token}` },
                        }
                      );

                      showSuccessToast(
                        response.data.message ||
                          `Document ${currentAction} successfully`
                      );

                      // Remove processed doc from table
                      setUploadedDocs((prev) =>
                        prev.filter((doc) => doc.id !== currentRequestId)
                      );

                      setShowActionModal(false);
                    } catch (error) {
                      console.error(`Error during ${currentAction}:`, error);
                      showErrorToast(
                        error.response?.data?.message ||
                          `Failed to ${currentAction} document`
                      );
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                >
                  {isSubmitting
                    ? currentAction === "approved"
                      ? "Approving..."
                      : "Rejecting..."
                    : currentAction.charAt(0).toUpperCase() +
                      currentAction.slice(1)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadedDocs;
